import { useCallback, useEffect, useId, useRef, useState, type MouseEvent } from "react";
import { Check, ChevronsUpDown, Pause, Play } from "lucide-react";
import { cn } from "@/components/ui/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Orb } from "@/components/ui/orb";

export interface ElevenLabsVoice {
  voiceId: string;
  name: string;
  category?: string;
  description?: string;
  previewUrl?: string;
  labels?: {
    accent?: string;
    description?: string;
    age?: string;
    gender?: string;
    "use case"?: string;
  };
}

export const DEFAULT_VOICES: ElevenLabsVoice[] = [
  {
    voiceId: "21m00Tcm4TlvDq8ikWAM",
    name: "Rachel",
    description: "Calm, conversational and warm",
    previewUrl: "https://storage.googleapis.com/eleven-public-cdn/audio/voices/rachel.mp3",
    labels: { accent: "American", gender: "Female", age: "Young", "use case": "Conversational" },
  },
  {
    voiceId: "pNInz6obpgDQGcFmaJgB",
    name: "Adam",
    description: "Deep, confident, and professional",
    previewUrl: "https://storage.googleapis.com/eleven-public-cdn/audio/voices/adam.mp3",
    labels: { accent: "American", gender: "Male", age: "Middle Aged", "use case": "Narration" },
  },
  {
    voiceId: "IKne3meq5aSn9XLyUdCD",
    name: "Charlie",
    description: "Casual, friendly, and natural",
    previewUrl: "https://storage.googleapis.com/eleven-public-cdn/audio/voices/charlie.mp3",
    labels: {
      accent: "Australian",
      gender: "Male",
      age: "Middle Aged",
      "use case": "Conversational",
    },
  },
  {
    voiceId: "XB0fDUnXU5powFXDhCwa",
    name: "Charlotte",
    description: "Warm, expressive, and clear",
    previewUrl: "https://storage.googleapis.com/eleven-public-cdn/audio/voices/charlotte.mp3",
    labels: { accent: "English", gender: "Female", age: "Young", "use case": "Characters" },
  },
  {
    voiceId: "N2lVS1w4EtoT3dr4eOWO",
    name: "Callum",
    description: "Intense, dramatic, and focused",
    previewUrl: "https://storage.googleapis.com/eleven-public-cdn/audio/voices/callum.mp3",
    labels: {
      accent: "Transatlantic",
      gender: "Male",
      age: "Middle Aged",
      "use case": "Characters",
    },
  },
  {
    voiceId: "TX3LPaxmHKxFdv7VOQHJ",
    name: "Liam",
    description: "Energetic, articulate, and youthful",
    previewUrl: "https://storage.googleapis.com/eleven-public-cdn/audio/voices/liam.mp3",
    labels: { accent: "American", gender: "Male", age: "Young", "use case": "Narration" },
  },
  {
    voiceId: "piTKgcLEGmPE4e6mEKli",
    name: "Nicole",
    description: "Soft, gentle, and whispery",
    previewUrl: "https://storage.googleapis.com/eleven-public-cdn/audio/voices/nicole.mp3",
    labels: { accent: "American", gender: "Female", age: "Young", "use case": "Audiobook" },
  },
  {
    voiceId: "jsCqWAovK2LkecY7zXl4",
    name: "Freya",
    description: "Confident, crisp, and engaging",
    previewUrl: "https://storage.googleapis.com/eleven-public-cdn/audio/voices/freya.mp3",
    labels: { accent: "British", gender: "Female", age: "Young", "use case": "News" },
  },
  {
    voiceId: "JBFqnCBsd6RMkjVDRZzb",
    name: "George",
    description: "Warm, story-rich British voice",
    previewUrl: "https://storage.googleapis.com/eleven-public-cdn/audio/voices/george.mp3",
    labels: { accent: "British", gender: "Male", age: "Middle Aged", "use case": "Storytelling" },
  },
];

export interface VoicePickerProps {
  voices?: ElevenLabsVoice[];
  value?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function VoicePicker({
  voices = DEFAULT_VOICES,
  value,
  onValueChange,
  placeholder = "Agent default",
  className,
  disabled = false,
  open,
  onOpenChange,
}: VoicePickerProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const listId = useId();

  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : internalOpen;
  const setIsOpen = isControlled ? onOpenChange : setInternalOpen;
  const pickerOpen = disabled ? false : isOpen;
  const selectedVoice = value ? voices.find((v) => v.voiceId === value) : undefined;
  const isAgentDefault = !value;

  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingVoiceId(null);
  }, []);

  useEffect(() => {
    return () => {
      stopAudio();
    };
  }, [stopAudio]);

  const togglePreview = useCallback(
    (e: MouseEvent, voice: ElevenLabsVoice) => {
      e.preventDefault();
      e.stopPropagation();

      if (!voice.previewUrl) return;

      if (playingVoiceId === voice.voiceId) {
        stopAudio();
      } else {
        stopAudio();
        try {
          const audio = new Audio(voice.previewUrl);
          audioRef.current = audio;
          setPlayingVoiceId(voice.voiceId);
          audio.onended = () => {
            setPlayingVoiceId(null);
            audioRef.current = null;
          };
          audio.onerror = () => {
            setPlayingVoiceId(null);
            audioRef.current = null;
          };
          audio.play().catch(() => {
            setPlayingVoiceId(null);
          });
        } catch {
          setPlayingVoiceId(null);
        }
      }
    },
    [playingVoiceId, stopAudio],
  );

  return (
    <Popover
      open={pickerOpen}
      onOpenChange={(next) => {
        if (disabled) return;
        if (!next) stopAudio();
        setIsOpen?.(next);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-controls={listId}
          aria-expanded={pickerOpen}
          className={cn("w-full justify-between gap-2", className)}
          disabled={disabled}
        >
          {selectedVoice ? (
            <div className="flex min-w-0 items-center gap-2 overflow-hidden">
              <div className="relative size-5 shrink-0">
                <Orb
                  agentState={playingVoiceId === selectedVoice.voiceId ? "talking" : "thinking"}
                  className="size-full"
                />
              </div>
              <span className="truncate font-medium">{selectedVoice.name}</span>
              {selectedVoice.labels?.accent && (
                <span className="text-muted-foreground text-xs">
                  ({selectedVoice.labels.accent})
                </span>
              )}
            </div>
          ) : (
            <span className="text-input-placeholder">{placeholder}</span>
          )}
          <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0 shadow-xl" align="start" side="top" sideOffset={8}>
        <Command>
          <CommandInput placeholder="Search voices by name or accent..." />
          <CommandList className="max-h-72" id={listId}>
            <CommandEmpty>No voice found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                className="flex cursor-pointer items-center gap-3 py-2"
                onSelect={() => {
                  onValueChange?.("");
                  setIsOpen?.(false);
                  stopAudio();
                }}
                value="agent default"
              >
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate font-medium text-sm">Agent default</span>
                  <span className="text-muted-foreground text-xs">
                    Use the agent's configured voice
                  </span>
                </div>
                <Check
                  className={cn("size-4 shrink-0", isAgentDefault ? "opacity-100" : "opacity-0")}
                />
              </CommandItem>
            </CommandGroup>
            <CommandGroup heading="ElevenLabs Voices">
              {voices.map((voice) => {
                const isSelected = value === voice.voiceId;
                const isPlaying = playingVoiceId === voice.voiceId;

                return (
                  <CommandItem
                    key={voice.voiceId}
                    value={`${voice.name} ${voice.labels?.accent ?? ""} ${voice.labels?.gender ?? ""}`}
                    onSelect={() => {
                      onValueChange?.(voice.voiceId);
                      setIsOpen?.(false);
                      stopAudio();
                    }}
                    className="flex items-center gap-3 py-2 cursor-pointer"
                  >
                    <button
                      type="button"
                      className="group/orb relative flex size-7 shrink-0 items-center justify-center overflow-hidden rounded-full"
                      onClick={(e) => togglePreview(e, voice)}
                      aria-label={
                        isPlaying ? `Pause ${voice.name} preview` : `Play ${voice.name} preview`
                      }
                      title={isPlaying ? "Pause preview" : "Play preview"}
                    >
                      <Orb
                        agentState={isPlaying ? "talking" : undefined}
                        className="pointer-events-none absolute inset-0 size-full"
                      />
                      <div
                        className={cn(
                          "absolute inset-0 flex items-center justify-center bg-background/70 backdrop-blur-xs transition-opacity",
                          isPlaying ? "opacity-100" : "opacity-0 group-hover/orb:opacity-100",
                        )}
                      >
                        {isPlaying ? (
                          <Pause className="size-3 fill-current text-icon-primary" />
                        ) : (
                          <Play className="size-3 fill-current text-icon-primary" />
                        )}
                      </div>
                    </button>

                    <div className="flex min-w-0 flex-1 flex-col">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate font-medium text-sm">{voice.name}</span>
                        {voice.labels?.gender && (
                          <span className="text-muted-foreground text-xs capitalize">
                            • {voice.labels.gender}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 text-muted-foreground text-xs">
                        {voice.labels?.accent && <span>{voice.labels.accent}</span>}
                        {voice.labels?.["use case"] && (
                          <>
                            <span>•</span>
                            <span className="truncate">{voice.labels["use case"]}</span>
                          </>
                        )}
                      </div>
                    </div>

                    <Check
                      className={cn("size-4 shrink-0", isSelected ? "opacity-100" : "opacity-0")}
                    />
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
